// @ts-nocheck
import { api, type HeadstoneDTO } from "../api/client";
import { getCurrentUser, refreshSession } from "../auth/session";
import { dtoToLegacy, type LegacyHeadstone } from "../utils/dto";
import { navigateToHeadstone } from "../map/headstone-nav";
import { closeAccountMenuModal } from "./account-menu";
import { openCommunityForHeadstone } from "./posts-feed";
import { t } from "../i18n";
import { translateApiMessage } from "../i18n/api-errors";
import { renderPetPhotoHtml } from "../utils/pet-photo-placeholder";
import { showView, getViewContainer } from "./view-router";
import { holeIdFromCoords } from "../utils/hole-id";
import { isPethernityActive } from "./pethernity";
import { formatDateForLocale } from "../i18n/format";

let profiloViewMounted = false;

type PresenceItem = {
  headstoneId: string;
  petName: string;
  photoUrl?: string | null;
  x: number;
  y: number;
  hasCandle: boolean;
  hasMessage: boolean;
  lastAt: string;
};

const PROFILE_PAW_SVG = `<svg width="34" height="34" viewBox="0 0 24 24" fill="#C6A24C" aria-hidden="true"><ellipse cx="7" cy="11" rx="2.1" ry="2.7"></ellipse><ellipse cx="12" cy="9.2" rx="2.1" ry="2.9"></ellipse><ellipse cx="17" cy="11" rx="2.1" ry="2.7"></ellipse><path d="M12 13c-2.6 0-4.6 1.8-4.6 3.8 0 1.5 1.3 2.2 4.6 2.2s4.6-.7 4.6-2.2C16.6 14.8 14.6 13 12 13z"></path></svg>`;

const PROFILE_HEART_SVG = `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C6A24C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7-4.5-9.3-9C1 8.5 3 5 6.3 5 8.3 5 9.7 6.2 12 8.8 14.3 6.2 15.7 5 17.7 5 21 5 23 8.5 21.3 12 19 16.5 12 21 12 21z"></path></svg>`;

const PROFILE_PLUS_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E6D2A0" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>`;

const CANDLE_STAT_SVG = `<svg width="26" height="30" viewBox="0 0 24 28" aria-hidden="true"><ellipse cx="12" cy="5" rx="2.6" ry="3.8" fill="#F4A93C"></ellipse><rect x="9" y="9" width="6" height="15" rx="1.6" fill="#F0E6CF"></rect><rect x="7" y="22.5" width="10" height="3" rx="1.5" fill="#D8CBA8"></rect></svg>`;

const CANDLE_ROW_SVG = `<svg width="16" height="18" viewBox="0 0 24 28" aria-hidden="true"><ellipse cx="12" cy="5" rx="2.6" ry="3.8" fill="#F4A93C"></ellipse><rect x="9" y="9" width="6" height="15" rx="1.6" fill="#F0E6CF"></rect><rect x="7" y="22.5" width="10" height="3" rx="1.5" fill="#D8CBA8"></rect></svg>`;

function buildProfileEmptyHtml(): string {
  return /* html */ `
    <div class="profile-empty">
      <div class="profile-empty__icon">${PROFILE_HEART_SVG}</div>
      <div class="profile-empty__title">${t("myHeadstones.emptyTitle")}</div>
      <p class="profile-empty__body">${t("myHeadstones.emptyBody")}</p>
      <button type="button" class="profile-empty__cta" data-profile-create>
        ${PROFILE_PLUS_SVG}
        <span>${t("myHeadstones.createCta")}</span>
      </button>
    </div>
  `;
}

function bindProfileEmptyCta(root: ParentNode): void {
  root.querySelector("[data-profile-create]")?.addEventListener("click", () => {
    window.openCreateMemorial();
  });
}

function presenceItemToLegacy(item: PresenceItem): LegacyHeadstone {
  return {
    id: item.headstoneId,
    holeId: holeIdFromCoords(item.x, item.y),
    name: item.petName,
    photo: item.photoUrl ?? null,
    dateLabel: "",
    fullDateLabel: "",
    metaLabel: "",
    epitaph: "",
    timestamp: new Date(item.lastAt).getTime(),
    paymentStatus: "paid",
    candleCount: 0,
  };
}

function openMemorialFromProfile(legacy: LegacyHeadstone, openModal = true): void {
  navigateToHeadstone(legacy, { openModal });
  showView("garden");
}

// Builds the full-screen Profilo shell once. The memorials list keeps its
// original #my-headstones-list id so the fetch/render logic is unchanged.
function ensureProfiloView(): void {
  const host = getViewContainer("profilo");
  if (!host || profiloViewMounted) return;

  host.innerHTML = /* html */ `
    <div class="profile-inner">
      <header class="profile-header">
        <button type="button" class="back-chip" onclick="goHome()">
          <i data-lucide="arrow-left"></i><span>${t("myHeadstones.backToGarden")}</span>
        </button>
        <div class="profile-header__row">
          <div class="profile-header__identity">
            <div class="profile-avatar">${PROFILE_PAW_SVG}</div>
            <div>
              <h1 class="profile-header__greeting" id="profile-greeting">Ciao</h1>
              <p class="profile-header__subtitle">${t("myHeadstones.subtitle")}</p>
            </div>
          </div>
          <button type="button" class="profile-logout" onclick="logoutCurrentUser()">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9A8FB0" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><path d="M16 17l5-5-5-5M21 12H9"></path></svg>
            <span>${t("myHeadstones.logout")}</span>
          </button>
        </div>
      </header>
      <div class="profile-body pscroll">
        <div class="eyebrow profile-section-label">${t("myHeadstones.myMemorials")}</div>
        <div id="my-headstones-list" class="cards-grid"></div>
        <div id="profile-pethernity" class="profile-pethernity"></div>

        <div class="eyebrow profile-section-label profile-section-label--presence">${t("myHeadstones.presenceTitle")}</div>
        <div id="profile-presence-stats" class="profile-presence-stats"></div>
        <div id="profile-presence-list" class="profile-presence-list"></div>
        <p id="profile-presence-empty" class="profile-presence-empty" hidden>${t("myHeadstones.presenceEmpty")}</p>
      </div>
    </div>
  `;

  profiloViewMounted = true;
}

function setGreeting(): void {
  const user = getCurrentUser();
  const el = document.getElementById("profile-greeting");
  if (!el) return;
  const name = user?.email ? user.email.split("@")[0] : "";
  el.textContent = name ? `Ciao, ${name}` : "Ciao";
  renderPethernityBanner();
}

function renderPethernityBanner(): void {
  const host = document.getElementById("profile-pethernity");
  if (!host) return;
  const user = getCurrentUser();
  if (isPethernityActive(user) && user?.pethernity?.expiresAt) {
    host.innerHTML = `<p class="profile-pethernity__status">${t("pethernity.activeUntil", {
      date: formatDateForLocale(user.pethernity.expiresAt.slice(0, 10)),
    })}</p>`;
    return;
  }
  host.innerHTML = `
    <button type="button" class="modal-btn" onclick="openPethernityPaywall()">
      ${t("pethernity.profileUpgrade")}
    </button>
  `;
}

// Back-compat: navigation away from the profile returns to the garden.
export function closeMyHeadstonesModal() {
  showView("garden");
}

function renderHeadstoneCard(headstone: HeadstoneDTO): HTMLElement {
  const legacy = dtoToLegacy(headstone);
  const item = document.createElement("div");
  item.className = "memorial-card profile-card";
  item.dataset.headstoneId = legacy.id;

  const epitaphHtml = legacy.epitaph
    ? `<span class="profile-card__epitaph">"${legacy.epitaph}"</span>`
    : "";

  item.innerHTML = `
    <button type="button" class="my-headstone-summary profile-card__main">
      <span class="profile-card__avatar">${renderPetPhotoHtml({
        variant: "avatar",
        photoUrl: legacy.photo,
        seed: legacy.id,
        name: legacy.name,
      })}</span>
      <span class="profile-card__info">
        <span class="profile-card__name">${legacy.name}</span>
        <span class="profile-card__sub">${
          legacy.dateLabel || legacy.metaLabel || t("myHeadstones.memorial")
        } · ${
          getCurrentUser()?.id === headstone.ownerId
            ? t("myHeadstones.createdByYou")
            : t("pethernity.sharedWithYou")
        }</span>
        ${epitaphHtml}
      </span>
    </button>
    <div class="profile-card__actions">
      <div class="profile-card__actions-row">
        <button type="button" class="profile-card__action profile-card__action--open my-headstone-open">${t("myHeadstones.open")}</button>
        <button type="button" class="profile-card__action profile-card__action--map my-headstone-map">${t("myHeadstones.goToMap")}</button>
      </div>
      <button type="button" class="profile-card__action profile-card__action--community my-headstone-community">${t("myHeadstones.shareCommunity")}</button>
    </div>
  `;

  item.querySelector(".my-headstone-summary")?.addEventListener("click", () => {
    openMemorialFromProfile(legacy, true);
  });

  item.querySelector(".my-headstone-open")?.addEventListener("click", (e) => {
    e.stopPropagation();
    openMemorialFromProfile(legacy, true);
  });

  item.querySelector(".my-headstone-map")?.addEventListener("click", (e) => {
    e.stopPropagation();
    openMemorialFromProfile(legacy, false);
  });

  item.querySelector(".my-headstone-community")?.addEventListener("click", (e) => {
    e.stopPropagation();
    openCommunityForHeadstone(legacy.id).catch(() => null);
  });

  return item;
}

async function loadMyHeadstones(): Promise<void> {
  const list = document.getElementById("my-headstones-list");
  if (!list) return;

  list.innerHTML = `<p class="my-headstones-empty">${t("myHeadstones.loading")}</p>`;

  try {
    const { data, error } = await api.GET("/headstones/me", {});
    if (error) throw error;
    const headstones = (data ?? []) as HeadstoneDTO[];

    if (!headstones.length) {
      list.innerHTML = buildProfileEmptyHtml();
      bindProfileEmptyCta(list);
      return;
    }

    list.innerHTML = "";
    headstones.forEach((headstone) => list.appendChild(renderHeadstoneCard(headstone)));
  } catch (err: any) {
    if (err?.message === "Unauthorized") {
      await refreshSession();
      showView("garden");
      window.openAuthModal("login", t("auth.sessionExpired"));
      return;
    }
    list.innerHTML = `<p class="my-headstones-empty">${t("myHeadstones.loadError", {
      message: translateApiMessage(err?.message) ?? err?.message,
    })}</p>`;
  }
}

function renderPresenceStats(candleCount: number): void {
  const stats = document.getElementById("profile-presence-stats");
  if (!stats) return;

  stats.innerHTML = `
    <div class="profile-presence-stat profile-presence-stat--candles">
      ${CANDLE_STAT_SVG}
      <div>
        <div class="profile-presence-stat__value">${candleCount}</div>
        <div class="profile-presence-stat__label">${t("myHeadstones.candlesLabel")}</div>
      </div>
    </div>
  `;
}

function renderPresenceList(items: PresenceItem[]): void {
  const list = document.getElementById("profile-presence-list");
  const empty = document.getElementById("profile-presence-empty");
  if (!list || !empty) return;

  list.innerHTML = "";

  if (!items.length) {
    empty.hidden = false;
    return;
  }

  empty.hidden = true;

  items.forEach((item) => {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "profile-presence-row";
    row.innerHTML = `
      <span class="profile-presence-row__avatar">${renderPetPhotoHtml({
        variant: "avatar",
        photoUrl: item.photoUrl,
        seed: item.headstoneId,
        name: item.petName,
      })}</span>
      <span class="profile-presence-row__name">${item.petName}</span>
      <span class="profile-presence-row__icon ${item.hasCandle ? "" : "is-inactive"}">${CANDLE_ROW_SVG}</span>
    `;

    row.addEventListener("click", () => {
      openMemorialFromProfile(presenceItemToLegacy(item), true);
    });

    list.appendChild(row);
  });
}

async function loadMyPresence(): Promise<void> {
  const stats = document.getElementById("profile-presence-stats");
  const list = document.getElementById("profile-presence-list");
  const empty = document.getElementById("profile-presence-empty");
  if (!stats || !list || !empty) return;

  stats.innerHTML = "";
  list.innerHTML = "";
  empty.hidden = true;

  try {
    const { data, error } = await api.GET("/users/me/presence", {});
    if (error) throw error;

    const presence = data as {
      candleCount: number;
      items: PresenceItem[];
    };

    renderPresenceStats(presence.candleCount ?? 0);
    renderPresenceList(presence.items ?? []);
  } catch (err: any) {
    if (err?.message === "Unauthorized") return;
    renderPresenceStats(0);
    empty.hidden = false;
  }
}

// Opens the full-screen Profilo view (auth-gated) and loads the user's memorials.
export async function openProfile(): Promise<void> {
  closeAccountMenuModal();
  const user = getCurrentUser();
  if (!user) {
    window.openAuthModal("login", t("auth.loginForTombs"));
    return;
  }

  ensureProfiloView();
  setGreeting();
  showView("profilo");
  await Promise.all([loadMyHeadstones(), loadMyPresence()]);
}

// Back-compat alias kept for window.openMyHeadstones / inline handlers.
export async function openMyHeadstones(): Promise<void> {
  await openProfile();
}
